import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowUpRight } from 'lucide-react'
import api from '../../utils/api'

/**
 * Real public collections, built by real collectors — not illustrative
 * category art. Pulls the top 4 (by item count) live from the archive.
 * Renders nothing if none load, no mock fallback.
 */
const useTopCollections = () => {
  const [state, setState] = useState({ collections: [], loaded: false })

  useEffect(() => {
    let live = true
    api.get('/collections', { params: { isPublic: true, limit: 4, page: 1 } })
      .then((res) => {
        if (!live) return
        const items = res.data?.data?.collections || []
        setState({ collections: Array.isArray(items) ? items.slice(0, 4) : [], loaded: true })
      })
      .catch(() => { if (live) setState({ collections: [], loaded: true }) })
    return () => { live = false }
  }, [])

  return state
}

const CategoriesSection = () => {
  const { collections, loaded } = useTopCollections()

  if (loaded && collections.length === 0) return null

  return (
    <section className="w-full bg-museum-ivory pt-20 pb-20 md:pt-28 md:pb-28">
      <div className="max-w-[1320px] mx-auto px-6 md:px-10">
        <motion.div
          className="max-w-xl mb-12 md:mb-16"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-[10px] uppercase tracking-[0.35em] text-museum-antique-gold font-sans font-semibold mb-5">
            02 &mdash; Index
          </p>
          <h2 className="font-editorial italic font-normal text-museum-black leading-[0.95]" style={{ fontSize: 'clamp(2.25rem, 4.4vw, 3.5rem)' }}>
            Explore the Archive
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-5">
          {!loaded
            ? [0, 1, 2, 3].map((i) => <div key={i} className="aspect-[3/4] bg-museum-black/5 animate-pulse" />)
            : collections.map((col, i) => (
                <motion.div
                  key={col._id || col.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: i * 0.1 }}
                >
                  <Link
                    to={`/collections/${col.slug || col._id}`}
                    className="group relative block aspect-[3/4] overflow-hidden bg-museum-black"
                  >
                    {col.coverImage && (
                      <img
                        src={col.coverImage}
                        alt={col.title}
                        className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-95 group-hover:scale-[1.03] transition-all duration-[900ms] ease-out"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-museum-black/95 via-museum-black/15 to-transparent" />

                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="block w-6 h-px bg-museum-antique-gold/0 group-hover:bg-museum-antique-gold/70 group-hover:w-10 mb-3 transition-all duration-700 ease-out" />
                      <div className="flex items-end justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-editorial italic text-museum-ivory text-lg leading-[1.15] mb-1.5 line-clamp-2">{col.title}</h3>
                          {typeof col.itemCount === 'number' && (
                            <p className="text-museum-ivory/55 text-xs font-sans tracking-wide">
                              {col.itemCount.toLocaleString()} items
                            </p>
                          )}
                        </div>
                        <ArrowUpRight
                          size={16}
                          className="text-museum-antique-gold opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-all duration-500 shrink-0"
                        />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
        </div>
      </div>
    </section>
  )
}

export default CategoriesSection
